/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router";
import { Link } from "../components/ui/Link";
import { Building2, CopyX, RefreshCw, Stethoscope } from "lucide-react";
import {
  listRegistryCases,
  REGISTRY_SIGNAL_LABELS,
  triggerRegistryScan,
  type RegistryDuplicateCase,
} from "../api/registryDuplicates";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { Badge, type BadgeTone } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Select } from "../components/ui/Select";
import { FilterBar } from "../components/FilterBar";
import { RegistryDirectory } from "../components/RegistryDirectory";
import { canEditRegistry } from "../components/RegistryBits";
import {
  filtersFromParams,
  filtersToScim,
  paramsWithFilters,
  type ActiveFilter,
} from "../lib/filters";
import { REGISTRY_DUPLICATE_FILTER_FIELDS } from "../lib/registryDuplicates";
import { REGISTRY_DUPLICATE_SORTS, paramsWithSort, sortChoice } from "../lib/sorting";

const STATUS_TONES: Record<RegistryDuplicateCase["status"], BadgeTone> = {
  OPEN: "info",
  DISMISSED: "neutral",
  MERGED: "success",
};

/**
 * The registry duplicate queue: suspected organization and provider pairs the scan surfaced.
 * Search, filters, sort, and paging work exactly as they do on the referrals list; without a
 * status filter the queue shows open cases only, and resolved history is one filter away.
 */
export function ReferralDuplicates() {
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromParams(REGISTRY_DUPLICATE_FILTER_FIELDS, searchParams);
  const setFilters = (next: ActiveFilter[]) =>
    setSearchParams(
      (previous) => paramsWithFilters(previous, REGISTRY_DUPLICATE_FILTER_FIELDS, next),
      { replace: true },
    );
  const filter = filtersToScim(REGISTRY_DUPLICATE_FILTER_FIELDS, filters);
  const sort = sortChoice(REGISTRY_DUPLICATE_SORTS, searchParams.get("sort") ?? "");
  const [scanning, setScanning] = useState(false);
  useChrome({}, []);

  async function scanNow() {
    setScanning(true);
    const result = await triggerRegistryScan();
    setScanning(false);
    if (!result.ok) {
      showToast("error", result.error.message);
      return;
    }
    showToast("success", "Scan queued. New cases appear within a minute; refresh to see them.");
  }

  const canAct = canEditRegistry(hasRole);
  const showsResolved = filters.some((active) => active.key === "status");

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Registry duplicates
        </h1>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className="text-[15px] leading-relaxed text-text-secondary sm:max-w-3xl sm:flex-1">
            Suspected duplicate organizations and providers, surfaced by exact contact matches and
            the identity-resolution engine. Open cases show by default; filter by status to see
            what was merged or dismissed.
          </p>
          {canAct && (
            <Button
              variant="secondary"
              size="md"
              className="self-end sm:ml-6 sm:shrink-0 sm:self-auto"
              disabled={scanning}
              onClick={() => void scanNow()}
            >
              <RefreshCw size={15} className={scanning ? "animate-spin" : undefined} />
              Scan now
            </Button>
          )}
        </div>
      </header>
      <RegistryDirectory
        countNoun="case"
        placeholder="Search by provider, organization, or case id"
        load={(q, page) => listRegistryCases(q, page, filter, sort)}
        filterKey={`${filter ?? ""}|${sort.value}`}
        controls={
          <Select
            className="w-full sm:w-44"
            value={sort.value}
            options={REGISTRY_DUPLICATE_SORTS}
            onChange={(value) =>
              setSearchParams((previous) => paramsWithSort(previous, value), { replace: true })
            }
            ariaLabel="Sort cases"
          />
        }
        filters={
          <FilterBar
            fields={REGISTRY_DUPLICATE_FILTER_FIELDS}
            active={filters}
            onChange={setFilters}
          />
        }
        itemKey={(duplicateCase) => duplicateCase.id}
        renderCard={(duplicateCase, index) => (
          <CaseCard
            key={duplicateCase.id}
            duplicateCase={duplicateCase}
            index={index}
            showStatus={showsResolved}
          />
        )}
        empty={{
          icon: <CopyX size={20} />,
          title: "No cases match",
          description:
            "Try another name or id, or clear the filters. The scan runs every six hours, or on demand.",
        }}
      />
    </div>
  );
}

function CaseCard({
  duplicateCase,
  index,
  showStatus,
}: {
  duplicateCase: RegistryDuplicateCase;
  index: number;
  showStatus: boolean;
}) {
  const organization = duplicateCase.entityType === "ORG";
  const Icon = organization ? Building2 : Stethoscope;
  return (
    <li
      className="enter"
      style={{ "--enter-delay": `${Math.min(index, 8) * 50 + 60}ms` } as CSSProperties}
    >
      <Link
        to={`/referrals/duplicates/${duplicateCase.id}`}
        className="border border-border bg-bg-elevated shadow-card lift flex h-full flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl px-4 py-3.5"
      >
        <Icon size={16} className="shrink-0 text-text-tertiary" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary">
            {duplicateCase.entityA.name ?? `#${duplicateCase.entityA.id}`}
            <span className="mx-2 text-text-tertiary">vs</span>
            {duplicateCase.entityB.name ?? `#${duplicateCase.entityB.id}`}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge tone="neutral">{organization ? "Organization" : "Provider"}</Badge>
            {showStatus && (
              <Badge tone={STATUS_TONES[duplicateCase.status]}>
                {duplicateCase.status.charAt(0) + duplicateCase.status.slice(1).toLowerCase()}
              </Badge>
            )}
            {duplicateCase.signals.map((signal) => (
              <Badge key={signal} tone="neutral">
                {REGISTRY_SIGNAL_LABELS[signal] ?? signal}
              </Badge>
            ))}
            <span className="text-xs text-text-tertiary">score {duplicateCase.score}</span>
          </div>
        </div>
        <span className="text-xs text-text-tertiary">Review</span>
      </Link>
    </li>
  );
}
